import * as JsonDecoder from "ts.data.json";

import { Controller } from "@hotwired/stimulus";
import { createRoot, Root } from "react-dom/client";
import React from "react";
import DispatchDashboard from "src/admin/dispatch/dashboard";
import { IDispatchClient, ReadonlyClient } from "src/admin/dispatch/dispatch_api_client";
import { Dashboard, DeliveryTracking, DeliveryTrackingStep } from "src/admin/dispatch/model";

export default class VirtualDispatchController extends Controller {
  static targets = ["form", "dashboard", "fileInput", "error", "banner"];

  declare fileInputTarget: HTMLInputElement;
  declare formTarget: HTMLElement;
  declare dashboardTarget: HTMLDivElement;
  declare errorTarget: HTMLElement;
  declare bannerTarget: HTMLElement;

  private dashboardRoot?: Root;
  private apiClient?: IDispatchClient;

  async loadDump(event: Event): Promise<void> {
    event.preventDefault();

    const file = this.fileInputTarget.files?.[0];
    if (!file) return;

    const textContent = await file.text();
    this.errorTarget.textContent = "";

    let json: { capturedAt: string; dashboard: unknown, driverTracking: Record<string, unknown> };
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      json = JSON.parse(textContent);
    } catch (e) {
      this.errorTarget.textContent = `Invalid JSON: ${(e as Error).message}`;
      return;
    }

    const dashboardResult = Dashboard.decoder.decode(json.dashboard);
    if (!dashboardResult.isOk()) {
      this.errorTarget.textContent = `Failed to decode dashboard: ${dashboardResult.error}`;
      return;
    }

    const driverTracking = new Map<number, DeliveryTracking[]>();
    for (const [locationKey, tracking] of Object.entries(json.driverTracking)) {
      const match = locationKey.match(/\d+/)

      if (match) {
        const extractedId = parseInt(match[0]);
        const decoded = VirtualDispatchController.trackingDecoder.decode(tracking);

        if (decoded.isOk()) {
          const unpackedTrackingDump = decoded.value;
          const unpackedDeliveries = Object.values(unpackedTrackingDump.deliveries).flat();

          const deliveryTracking = Object.values(unpackedDeliveries).flat().map(row => ({
            uid: row.deliveryUid,
            liveLastUpdate: unpackedTrackingDump.receivedAt,
            liveArrivalEstimate: new Date(unpackedTrackingDump.receivedAt.getTime() + 1000 * row.secondsToDropoff),
            routeToPickup: row.routeToPickup as DeliveryTrackingStep[],
            routeToDropoff: row.routeToDropoff as DeliveryTrackingStep[],
          }));

          driverTracking.set(extractedId, deliveryTracking as DeliveryTracking[]);
        }
      }
    }

    const capturedAt = new Date(json.capturedAt);
    this.apiClient = new ReadonlyClient(-1, "", dashboardResult.value, driverTracking);
    this.apiClient.start();

    this.bannerTarget.textContent = `Virtual dashboard — frozen at ${capturedAt.toLocaleString()}`;
    this.formTarget.classList.add("is-hidden");
    this.dashboardTarget.classList.remove("is-hidden");

    this.dashboardRoot = createRoot(this.dashboardTarget);
    this.dashboardRoot.render(
      React.createElement(DispatchDashboard, {
        apiClient: this.apiClient,
        currentAdminId: 0,
        fixedTime: new Date(json.capturedAt),
      })
    );
  }

  disconnect(): void {
    this.dashboardRoot?.unmount();
    this.dashboardRoot = undefined;
    this.apiClient?.stop();
  }

  static readonly trackingDecoder = JsonDecoder.object({
    driverId: JsonDecoder.number(),
    receivedAt: JsonDecoder.string().map(sd => new Date(sd)),
    deliveries: JsonDecoder.record(
      JsonDecoder.array(
        JsonDecoder.object({
          deliveryUid: JsonDecoder.string(),
          secondsToDropoff: JsonDecoder.number(),
          routeToDropoff: JsonDecoder.succeed(),
          routeToPickup: JsonDecoder.succeed()
        }, "TrackingDelivery"),
        "TrackingDelivery[]"),
      "TrackingDeliveryRecord")
  }, "DumpedTracking");
}
